/**
 * ConferenceRecapScreen — Post-Conference Intelligence
 *
 * AI-synthesized recap of the entire conference experience:
 * Executive summary, action items, follow-up introductions,
 * curated reading list, and key insights — all with agentic actions.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  ArrowLeft, Share2, Download, Sparkles, FileText, CheckSquare,
  Users, BookOpen, Clock, ChevronDown, ChevronUp, Play,
  Check, Loader2, Calendar, Linkedin, Mail, Coffee,
  BookMarked, CircleDot, AlertCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import BottomNav from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import { notify } from '../utils/notifications';
import {
  generateConferenceRecap,
  getAgentActionDescription,
  type ConferenceRecap,
  type RecapItem,
} from '../services/aiService';
import { eventService } from '../services/dataService';
import type { AgentActionType } from '../context/AppContext';
import { useEventModules } from '../hooks/useEventModules';
import { ReportAIResponseButton } from '../components/ReportAIResponseButton';

// ─── Icon resolver ──────────────────────────────────────────────────────────

const SECTION_ICONS: Record<string, React.FC<{ className?: string; style?: React.CSSProperties }>> = {
  FileText, CheckSquare, Users, BookOpen, Sparkles,
};

const ACTION_ICONS: Record<string, React.FC<{ className?: string }>> = {
  'schedule-meeting': Calendar,
  'draft-followup': Mail,
  'block-time': Clock,
  'add-linkedin': Linkedin,
  'send-intro': Coffee,
  'save-reading-list': BookMarked,
  'export-recap': Download,
};

const PRIORITY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  high: { bg: 'var(--app-danger-dim)', text: 'var(--app-danger)', border: 'var(--app-danger-border)' },
  medium: { bg: 'var(--app-warning-dim)', text: 'var(--app-warning)', border: 'var(--app-warning)' },
  low: { bg: 'var(--app-accent-dim)', text: 'var(--app-accent)', border: 'var(--app-accent-border)' },
};

// ─── Agent Action Button ────────────────────────────────────────────────────

function AgentActionButton({
  item,
  onExecute,
}: {
  item: RecapItem;
  onExecute: (item: RecapItem) => void;
}) {
  const [status, setStatus] = useState<'idle' | 'running' | 'completed'>('idle');
  const ActionIcon = ACTION_ICONS[item.actionType || ''] || Play;

  const handleClick = async () => {
    if (status !== 'idle') return;
    setStatus('running');
    onExecute(item);
    // Simulate completion after context executes
    setTimeout(() => setStatus('completed'), 2200);
  };

  return (
    <button
      onClick={handleClick}
      disabled={status !== 'idle'}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] active:scale-95 transition-all shrink-0"
      style={{
        background: status === 'completed'
          ? 'var(--app-success-dim)'
          : status === 'running'
          ? 'var(--app-accent-dim)'
          : 'var(--app-accent-dim)',
        color: status === 'completed' ? 'var(--app-success)' : 'var(--app-accent)',
        border: `1px solid ${status === 'completed' ? 'var(--app-success-border)' : 'var(--app-accent-border)'}`,
      }}
    >
      {status === 'running' ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : status === 'completed' ? (
        <Check className="w-3 h-3" />
      ) : (
        <ActionIcon className="w-3 h-3" />
      )}
      <span>
        {status === 'running'
          ? 'Working...'
          : status === 'completed'
          ? 'Done'
          : item.actionLabel || 'Execute'}
      </span>
    </button>
  );
}

// ─── Recap Item Row ─────────────────────────────────────────────────────────

function RecapItemRow({
  item,
  onToggle,
  onExecuteAction,
  allowAgentActions,
}: {
  item: RecapItem;
  onToggle?: (id: string) => void;
  onExecuteAction: (item: RecapItem) => void;
  allowAgentActions: boolean;
}) {
  const priority = PRIORITY_COLORS[item.priority || 'medium'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex gap-3 p-3 rounded-xl border"
      style={{
        background: 'var(--app-bg)',
        borderColor: 'var(--app-border)',
      }}
    >
      {/* Checkbox for action items */}
      {onToggle && (
        <button
          onClick={() => onToggle(item.id)}
          className="mt-0.5 shrink-0"
        >
          <div
            className="size-5 rounded-md border-2 flex items-center justify-center transition-colors"
            style={{
              borderColor: item.completed ? 'var(--app-success)' : 'var(--app-text-muted)',
              background: item.completed ? 'var(--app-success-dim)' : 'transparent',
            }}
          >
            {item.completed && <Check className="w-3 h-3" style={{ color: 'var(--app-success)' }} />}
          </div>
        </button>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className="text-xs"
            style={{
              color: item.completed ? 'var(--app-text-muted)' : 'var(--app-text)',
              textDecoration: item.completed ? 'line-through' : 'none',
            }}
          >
            {item.text}
          </p>
          {item.priority && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full shrink-0 uppercase tracking-wider"
              style={{
                background: priority.bg,
                color: priority.text,
              }}
            >
              {item.priority}
            </span>
          )}
        </div>
        {item.subtitle && (
          <p className="text-[11px] mt-1" style={{ color: 'var(--app-text-muted)' }}>
            {item.subtitle}
          </p>
        )}

        {/* Agent Action */}
        {allowAgentActions && item.actionType && !item.completed && (
          <div className="mt-2">
            <AgentActionButton item={item} onExecute={onExecuteAction} />
          </div>
        )}
      </div>
    </motion.div>
  );
}

// ─── Markdown Renderer ──────────────────────────────────────────────────────

function MarkdownContent({ content }: { content: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      {content.split('\n').map((line, i) => {
        if (line.startsWith('### '))
          return <p key={i} className="text-xs mt-2 mb-0.5" style={{ color: 'var(--app-text)', opacity: 0.95 }}>{line.replace('### ', '')}</p>;
        if (line.startsWith('**') && line.endsWith('**'))
          return <p key={i} className="text-xs mt-1.5" style={{ color: 'var(--app-text)' }}>{line.replace(/\*\*/g, '')}</p>;
        if (line.startsWith('- '))
          return <p key={i} className="text-[11px] pl-2" style={{ color: 'var(--app-text-muted)' }}>  {"•"} {line.replace('- ', '')}</p>;
        if (line.match(/^\d\./))
          return <p key={i} className="text-[11px] pl-2" style={{ color: 'var(--app-text-muted)' }}>{line}</p>;
        return line.trim() ? <p key={i} className="text-[11px]" style={{ color: 'var(--app-text-subtle)' }}>{line.replace(/\*\*/g, '')}</p> : <div key={i} className="h-1" />;
      })}
    </div>
  );
}

// ─── Section Component ──────────────────────────────────────────────────────

function RecapSectionCard({
  section,
  expandedSections,
  toggleSection,
  onToggleItem,
  onExecuteAction,
  allowAgentActions,
}: {
  section: ConferenceRecap['sections'][0];
  expandedSections: Set<string>;
  toggleSection: (id: string) => void;
  onToggleItem: (sectionId: string, itemId: string) => void;
  onExecuteAction: (item: RecapItem) => void;
  allowAgentActions: boolean;
}) {
  const isExpanded = expandedSections.has(section.id);
  const SectionIcon = SECTION_ICONS[section.icon] || FileText;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border overflow-hidden"
      style={{
        background: 'var(--app-surface)',
        borderColor: 'var(--app-border)',
        boxShadow: 'var(--app-card-shadow)',
      }}
    >
      {/* Section Header */}
      <button
        onClick={() => toggleSection(section.id)}
        className="w-full flex items-center justify-between px-4 py-3.5 active:opacity-80 transition-opacity"
      >
        <div className="flex items-center gap-3">
          <div
            className="size-9 rounded-xl flex items-center justify-center"
            style={{ background: 'var(--app-accent-dim)' }}
          >
            <SectionIcon className="w-4.5 h-4.5" style={{ color: 'var(--app-accent)' }} />
          </div>
          <div className="text-left">
            <p className="text-sm" style={{ color: 'var(--app-text)' }}>{section.title}</p>
            {section.items && section.items.length > 0 && (
              <p className="text-[10px]" style={{ color: 'var(--app-text-muted)' }}>
                {section.items.length} item{section.items.length !== 1 ? 's' : ''}
                {section.type === 'actions' && ` · ${section.items.filter(i => i.completed).length} done`}
              </p>
            )}
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4" style={{ color: 'var(--app-text-muted)' }} />
        ) : (
          <ChevronDown className="w-4 h-4" style={{ color: 'var(--app-text-muted)' }} />
        )}
      </button>

      {/* Section Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--app-border)' }}>
              {/* Content markdown */}
              <div className="pt-3">
                <MarkdownContent content={section.content} />
              </div>

              {/* Items */}
              {section.items && section.items.length > 0 && (
                <div className="flex flex-col gap-2 mt-3">
                  {section.items.map(item => (
              <RecapItemRow
                      key={item.id}
                      item={item}
                      onToggle={section.type === 'actions' ? () => onToggleItem(section.id, item.id) : undefined}
                onExecuteAction={onExecuteAction}
                allowAgentActions={allowAgentActions}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Main Screen ────────────────────────────────────────────────────────────

export default function ConferenceRecapScreen() {
  const navigate = useNavigate();
  const {
    addNote,
    executeAgentAction,
    aiPreferences,
    currentEventId,
    authSession,
    isAuthSessionCurrent,
  } = useApp();
  const { isEnabled } = useEventModules();
  const [recap, setRecap] = useState<ConferenceRecap | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [progressLabel, setProgressLabel] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['summary']));
  const [eventName, setEventName] = useState<string>('Conference');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const owner = authSession;
    if (!owner || !isAuthSessionCurrent(owner)) {
      setIsLoading(false);
      return;
    }
    let disposed = false;
    const stillCurrent = () =>
      !disposed && isAuthSessionCurrent(owner);
    setRecap(null);
    setIsLoading(true);
    if (currentEventId) {
      eventService.getById(currentEventId, { signal: owner.signal })
        .then((ev) => {
          if (ev && stillCurrent()) {
            setEventName((ev as any).name ?? 'Conference');
          }
        })
        .catch(() => {});
    }
    generateConferenceRecap(
      currentEventId ?? '',
      (status) => {
        if (stillCurrent()) setProgressLabel(status);
      },
      owner.signal,
    ).then(data => {
      if (!stillCurrent()) return;
      setRecap(data);
      setIsLoading(false);
      setProgressLabel('');
    }).catch(() => {
      if (stillCurrent()) {
        setIsLoading(false);
        setProgressLabel('');
      }
    });
    return () => { disposed = true; };
  }, [authSession, currentEventId, isAuthSessionCurrent]);

  const toggleSection = (id: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleToggleItem = (sectionId: string, itemId: string) => {
    if (!recap) return;
    setRecap({
      ...recap,
      sections: recap.sections.map(s =>
        s.id === sectionId
          ? { ...s, items: s.items?.map(i => (i.id === itemId ? { ...i, completed: !i.completed } : i)) }
          : s
      ),
    });
  };

  const handleExecuteAction = async (item: RecapItem) => {
    if (!isEnabled('notes')) {
      notify.info('Notes are not available for this event.');
      return;
    }
    const session = authSession;
    if (!item.actionType || !session || !isAuthSessionCurrent(session)) return;
    const description = getAgentActionDescription(item.actionType, item.metadata);

    try {
      await executeAgentAction({
        type: item.actionType as AgentActionType,
        title: item.actionLabel || 'Executing action',
        description,
        icon: item.actionType,
        metadata: item.metadata,
      }, session);
      if (!isAuthSessionCurrent(session)) return;
      notify.success('Saved to My Notes', description);
    } catch (err) {
      if (isAuthSessionCurrent(session)) {
        notify.error('Could not complete that action', err instanceof Error ? err.message : 'Please try again.');
      }
    }
  };

  const handleSaveAsNote = () => {
    if (!isEnabled('notes')) {
      notify.info('Notes are not available for this event.');
      return;
    }
    const session = authSession;
    if (!recap || !session || !isAuthSessionCurrent(session)) return;
    const content = recap.sections
      .map(s => `## ${s.title}\n\n${s.content}${s.items?.length ? '\n\n' + s.items.map(i => `- ${i.text}`).join('\n') : ''}`)
      .join('\n\n---\n\n');
    const saved = addNote({
      title: `Conference Recap — ${eventName}`,
      content,
      source: 'ai-report',
      sourceLabel: 'AI Conference Recap',
    }, session);
    if (saved) notify.success('Saved as note', 'Find it in My Notes');
  };

  const handleExpandAll = () => {
    if (!recap) return;
    setExpandedSections(new Set(recap.sections.map(s => s.id)));
  };

  return (
    <div
      className="size-full flex flex-col overflow-hidden"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      {/* Header */}
      <header
        aria-label="Conference recap"
        className="app-screen-header flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 pb-3 border-b shrink-0 backdrop-blur-md"
        style={{
          backgroundColor: 'var(--app-nav-bg)',
          borderColor: 'var(--app-divider)',
        }}
      >
        <div className="flex min-w-0 items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="size-9 shrink-0 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-muted)' }}
            aria-label="Go back"
          >
            <ArrowLeft className="w-4.5 h-4.5" style={{ color: 'var(--app-text)' }} />
          </button>
          <div className="min-w-0">
            <p className="text-sm" style={{ color: 'var(--app-text)' }}>Conference Recap</p>
            <p className="text-[10px]" style={{ color: 'var(--app-text-muted)' }}>
              Powered by AI
            </p>
          </div>
        </div>
        <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-1 [&>button]:ml-0 [&>button]:mt-0">
          {recap?.receiptId && <ReportAIResponseButton receiptId={recap.receiptId} />}
          {isEnabled('notes') && (
          <button
            onClick={handleSaveAsNote}
            className="size-9 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-muted)' }}
            aria-label="Save as note"
          >
            <Download className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
          </button>
          )}
          <button
            onClick={() => notify.info('Share', 'Share functionality coming soon')}
            className="size-9 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-muted)' }}
            aria-label="Share recap"
          >
            <Share2 className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
          </button>
        </div>
      </header>

      {/* Content */}
      <div ref={scrollRef} className="app-nav-content flex-1 overflow-y-auto">
        {isLoading ? (
          /* Loading State */
          <div className="flex flex-col items-center justify-center h-full gap-4 px-8">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="size-16 rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, var(--app-accent-solid), var(--app-accent))',
                boxShadow: '0 0 40px var(--app-accent-glow)',
              }}
            >
              <Sparkles className="w-7 h-7 text-white" />
            </motion.div>
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--app-text)' }}>
                Generating Your Recap
              </p>
              <motion.p
                key={progressLabel}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs mt-1"
                style={{ color: 'var(--app-text-muted)' }}
              >
                {progressLabel || 'Analyzing your conference experience...'}
              </motion.p>
            </div>

            {/* Progress steps */}
            <div className="w-full max-w-[240px] flex flex-col gap-2 mt-2">
              {[
                ...(isEnabled('agenda') ? ['Activity'] : []),
                ...(isEnabled('notes') ? ['Notes', 'Actions'] : []),
                ...(isEnabled('attendees') ? ['Connections'] : []),
                ...(isEnabled('papers') ? ['Reading List'] : []),
                'Insights',
              ].map((step, i) => (
                <motion.div
                  key={step}
                  initial={{ opacity: 0.3 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.5 }}
                  className="flex items-center gap-2"
                >
                  <CircleDot className="w-3 h-3" style={{ color: 'var(--app-accent)' }} />
                  <span className="text-[10px]" style={{ color: 'var(--app-text-muted)' }}>
                    {step}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        ) : recap ? (
          <div className="flex flex-col gap-4 p-4">
            {/* Hero Card */}
            <div
              className="rounded-xl p-5 border relative overflow-hidden"
              style={{
                background: 'var(--app-surface)',
                borderColor: 'var(--app-border)',
                boxShadow: 'var(--app-card-shadow)',
              }}
            >
              {/* Glow decoration */}
              <div
                className="absolute top-0 right-0 size-40 rounded-full -translate-y-12 translate-x-12 pointer-events-none blur-3xl"
                style={{ backgroundColor: 'var(--app-accent-glow)' }}
              />

              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
                  <span className="text-[10px] uppercase tracking-widest" style={{ color: 'var(--app-accent)' }}>
                    AI-Generated Recap
                  </span>
                </div>
                <h1 className="text-xl" style={{ color: 'var(--app-text)' }}>{recap.title}</h1>
                <p className="text-xs mt-1" style={{ color: 'var(--app-text-muted)' }}>{recap.subtitle}</p>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {[
                    ...(isEnabled('agenda') ? [{ label: 'Sessions', value: recap.stats.sessionsAttended, icon: Calendar }] : []),
                    ...(isEnabled('notes') ? [{ label: 'Notes', value: recap.stats.notesCreated, icon: FileText }] : []),
                    ...(isEnabled('attendees') ? [{ label: 'Connections', value: recap.stats.connectionsMade, icon: Users }] : []),
                    ...(isEnabled('papers') ? [{ label: 'Papers', value: recap.stats.papersReviewed, icon: BookOpen }] : []),
                  ].map(stat => (
                    <div
                      key={stat.label}
                      className="flex flex-col items-center gap-1 py-2.5 rounded-lg"
                      style={{ background: 'var(--app-bg)' }}
                    >
                      <stat.icon className="w-3.5 h-3.5" style={{ color: 'var(--app-accent)' }} />
                      <span className="text-lg" style={{ color: 'var(--app-text)' }}>{stat.value}</span>
                      <span className="text-[9px]" style={{ color: 'var(--app-text-muted)' }}>{stat.label}</span>
                    </div>
                  ))}
                </div>

                {/* Quick actions */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={handleExpandAll}
                    className="flex-1 py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
                    style={{
                      background: 'var(--app-accent-dim)',
                      color: 'var(--app-accent)',
                      border: '1px solid var(--app-accent-border)',
                    }}
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                    Expand All
                  </button>
                  {isEnabled('notes') && (
                  <button
                    onClick={handleSaveAsNote}
                    className="flex-1 py-2.5 rounded-xl text-xs flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
                    style={{
                      background: 'var(--app-accent-solid)',
                      color: '#fff',
                    }}
                  >
                    <Download className="w-3.5 h-3.5" />
                    Save as Note
                  </button>
                  )}
                </div>
              </div>
            </div>

            {/* AI paused banner */}
            {!aiPreferences.aiSuggestions && (
              <div
                className="rounded-xl px-4 py-3 flex items-center gap-3 border"
                style={{
                  background: 'var(--app-warning-dim)',
                  borderColor: 'var(--app-warning)',
                }}
              >
                <AlertCircle className="w-4 h-4 shrink-0" style={{ color: 'var(--app-warning)' }} />
                <p className="text-[11px]" style={{ color: 'var(--app-warning)' }}>
                  AI suggestions are paused. Some agentic actions may be limited.{' '}
                  <button
                    onClick={() => navigate('/profile')}
                    className="underline"
                  >
                    Re-enable in Profile
                  </button>
                </p>
              </div>
            )}

            {/* Sections */}
            {recap.sections.map((section, i) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
              >
                <RecapSectionCard
                  section={section}
                  expandedSections={expandedSections}
                  toggleSection={toggleSection}
                  onToggleItem={handleToggleItem}
                onExecuteAction={handleExecuteAction}
                allowAgentActions={isEnabled('notes')}
                />
              </motion.div>
            ))}

            {/* Footer */}
            <div className="text-center py-4">
              <p className="text-[10px]" style={{ color: 'var(--app-text-muted)' }}>
                Generated by AI Copilot · {new Date(recap.generatedAt).toLocaleString()}
              </p>
              <p className="text-[10px] mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                Based on your sessions, notes, and connections
              </p>
            </div>
          </div>
        ) : null}
      </div>

      <BottomNav />
    </div>
  );
}
